import { type Monitor, Signal, batch } from "@suse-o11y/signal";
import uniqBy from "lodash/uniqBy";

import type { PinnedItem } from "../PinnedItem";

export interface InitialPinnedItemsSectionState<TItemData> {
	/**
	 * @default false
	 */
	isExpanded?: boolean;
	/**
	 * @default true
	 */
	shouldCollapseAllOnPin?: boolean;
	/**
	 * @default []
	 */
	items?: ReadonlyArray<TItemData>;

	getItemIdentifier: (item: TItemData) => string;
}

export class PinnedItemsSectionState<TItemData> {
	public static init<TItemData>({
		isExpanded = false,
		shouldCollapseAllOnPin = true,
		items = [],
		getItemIdentifier,
	}: InitialPinnedItemsSectionState<TItemData>): PinnedItemsSectionState<TItemData> {
		return new PinnedItemsSectionState(
			Signal(isExpanded),
			Signal(
				items.map((data) => ({
					identifier: getItemIdentifier(data),
					isExpanded: Signal(false),
					data,
				})),
			),
			shouldCollapseAllOnPin,
			getItemIdentifier,
		);
	}

	private constructor(
		public readonly isExpanded: Signal<boolean>,
		/**
		 * The identifier field is necessary because some ItemData does not have an id,
		 * so we have to create one based on the data, ex: by passing the entire item to toJSON().
		 */
		private readonly items: Signal<ReadonlyArray<{ identifier: string } & PinnedItem<TItemData>>>,
		private readonly shouldCollapseAllOnPin: boolean,
		private readonly getItemIdentifier: (item: TItemData) => string,
	) {}

	public expandAll(isExpanded: boolean): void {
		batch((monitor) => {
			if (isExpanded) {
				this.isExpanded.write(true);
			}

			this.items.read(monitor).forEach((item) => {
				item.isExpanded.write(isExpanded);
			});
		});
	}

	public pin(pinningItem: TItemData): void {
		batch(() => {
			this.isExpanded.write(true);

			if (this.shouldCollapseAllOnPin) {
				this.expandAll(false);
			}

			this.items.write((current) => {
				return uniqBy(
					[
						// should be expanded when pinned
						{
							identifier: this.getItemIdentifier(pinningItem),
							isExpanded: Signal(true),
							data: pinningItem,
						},
						...current,
					],
					({ identifier }) => identifier,
				);
			});
		});
	}

	public unpin(unpinningItem: TItemData): void {
		const unpinningItemIdentifier = this.getItemIdentifier(unpinningItem);

		this.items.write((items) => items.filter(({ identifier }) => identifier !== unpinningItemIdentifier));
	}

	public unpinAll(): void {
		this.items.write([]);
	}

	public getItems(monitor: Monitor): ReadonlyArray<{ identifier: string } & PinnedItem<TItemData>> {
		return this.items.read(monitor);
	}

	public isPinned(monitor: Monitor, item: TItemData): boolean {
		const itemIdentifier = this.getItemIdentifier(item);

		return this.getItems(monitor).some(({ identifier }) => identifier === itemIdentifier);
	}
}
